import { Store } from 'vuex';
import { AppState } from '@/store';
import { isDefined, generateRandomNumberString } from '@/helpers';
import { ui } from '@/constants';
import { MUTATIONS } from '@/store/adobe';

export interface UtilsService {
  formatPrice(price: number | null | undefined, isShortCurrency?: boolean | undefined): string;
  isDeliveryOnline(): boolean;
  isOrderingOnline(): boolean;
  checkDidSwitchDesktopTablet(): boolean;
  refreshAdobeTargetExperiences(): Promise<void>;
  targetView(view: string): void;
  targetEvent(event: string): void;
  iOSDevice(): boolean;
  androidDevice():boolean;
  setIsFromBag(isFromBag: boolean): void;
  setIsMakeItMyOwn(isMakeItMyOwn: boolean): void;
  generateRandomNumberString(length: number): string;
}

export class AppUtilsService implements UtilsService {
  previousClientWidth: number;

  constructor(private store: Store<AppState>) { 
    this.previousClientWidth = this.store.state.ui?.clientWidth || document.body.clientWidth;
  }

  formatPrice(price?: number, isShortCurrency = false): string {
    if (!isDefined(price)) {
      return '';
    }

    const currencySymbol = isShortCurrency ? this.store.state.ui.countryProfile.currencySymbolShort : this.store.state.ui.countryProfile.currencySymbol;
    return `${currencySymbol}${price.toFixed(2)}`;
  }

  isDeliveryOnline(): boolean {
    const flags = this.store.state.featureFlags;
    if (flags.orderingStatus && (!flags.orderingStatus.isWebDeliveryOnline || !flags.orderingStatus.deliveryOnline)) {
      return false;
    }

    return true;
  }

  isOrderingOnline(): boolean {
    const flags = this.store.state.featureFlags;
    if (flags.orderingStatus && (!flags.orderingStatus.isWebOrderingOnline || !flags.orderingStatus.isOnline)) {
      return false;
    }

    return true;
  }

  // Called _only_ in the resize listener in cmg-app.ts, must be called from within a debounced function
  checkDidSwitchDesktopTablet(): boolean {
    const didSwitch =
      (this.previousClientWidth <= ui.breakpoints.desktop && this.store.state.ui.clientWidth > ui.breakpoints.desktop) ||
      (this.previousClientWidth > ui.breakpoints.desktop && this.store.state.ui.clientWidth <= ui.breakpoints.desktop);
    this.previousClientWidth = this.store.state.ui.clientWidth;
    return didSwitch;
  }

  iOSDevice(): boolean {
    return (
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
      // iPad on iOS 13 detection
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  }
   androidDevice(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;
    return /android/i.test(userAgent);
  }

  setIsFromBag(isFromBag: boolean): void {
    this.store.commit(MUTATIONS.SET_IS_FROM_BAG, isFromBag);
  }
  
  setIsMakeItMyOwn(isMakeItMyOwn: boolean): void {
    this.store.commit(MUTATIONS.SET_IS_MAKE_IT_MY_OWN, isMakeItMyOwn);
  }

  generateRandomNumberString(length: number): string {
    return generateRandomNumberString(length);
  }

  // Only takes in the global `adobe` so that it can be tested
  async refreshAdobeTargetExperiences(): Promise<void> {
    // @ts-ignore - `adobe` is pulled in from a script as a global
    if (typeof adobe != 'undefined' && adobe.target && typeof adobe.target.getOffers === 'function' && typeof adobe.target.applyOffers === 'function') {
      // @ts-ignore - `adobe` is pulled in from a script as a global
      const offers = await adobe.target.getOffers({
        request: {
          execute: {
            pageLoad: {
              parameters: {},
            },
          },
        },
      });
      // @ts-ignore - `adobe` is pulled in from a script as a global
      await adobe.target.applyOffers({ response: offers });
    }
  }

  targetView(view: string): void {
    // @ts-ignore - `adobe` is pulled in from a script as a global
    if (typeof adobe != 'undefined' && adobe.target && typeof adobe.target.triggerView === 'function') {
      // @ts-ignore - `adobe` is pulled in from a script as a global
      adobe.target.triggerView(view);
    }
  }

  targetEvent(event: string): void {
    // @ts-ignore - `adobe` is pulled in from a script as a global
    if (typeof adobe != 'undefined' && adobe.target && typeof adobe.target.trackEvent === 'function') {
      // @ts-ignore - `adobe` is pulled in from a script as a global
      adobe.target.trackEvent({ 'mbox': event });
    }
  }
}
